<template>
  <div class="output-sidebar">

    <!-- ── Empty state ── -->
    <div v-if="!output" class="output-empty">
      <svg class="output-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18" />
        <path d="M9 21V9" />
      </svg>
      <p class="output-empty-title">Outputs will appear here</p>
      <p class="output-empty-subtitle">Complete the chat to generate your entry point</p>
    </div>

    <!-- ── Populated state ── -->
    <template v-else>

      <!-- Header -->
      <div v-if="showReadyHeader" class="output-header">
        <div class="output-header-left">
          <span class="output-status-dot"></span>
          <span class="output-header-title">Entry Point Ready</span>
        </div>
      </div>

      <div class="output-body">

        <!-- ── KEYWORD ── -->
        <div class="output-card">
          <div class="output-card-label">KEYWORD</div>
          <div class="output-field-row">
            <span v-if="!editing.keyword" class="output-field-value output-mono">{{ drafts.keyword }}</span>
            <input
              v-else v-model="drafts.keyword"
              class="output-inline-input output-mono"
              @keyup.enter="saveField('keyword')"
              @keyup.esc="cancelField('keyword')"
            />
            <div class="output-field-actions">
              <template v-if="editing.keyword">
                <button class="output-icon-btn output-icon-btn--confirm" :disabled="!drafts.keyword.trim() || validating.keyword" @click="saveField('keyword')">
                  <i :class="validating.keyword ? 'fa fa-spinner fa-spin' : 'fa fa-check'"></i>
                </button>
                <button class="output-icon-btn" @click="cancelField('keyword')"><i class="fa fa-times"></i></button>
              </template>
              <button class="output-icon-btn" :class="{ 'output-icon-btn--copied': copiedKey === 'keyword' }" @click="copy(drafts.keyword, 'keyword', null, 'keyword')">
                <i :class="copiedKey === 'keyword' ? 'fa fa-check' : 'fa fa-copy'"></i>
                <span v-if="copiedKey === 'keyword'" class="output-copied-label">Copied!</span>
              </button>
            </div>
          </div>
          <div v-if="errors.keyword" class="output-validation-error"><i class="fa fa-exclamation-circle"></i> {{ errors.keyword }}</div>
          <button v-if="!editing.keyword" class="output-click-edit" @click="startEdit('keyword')">Click to edit</button>
        </div>

        <!-- ── AD ID ── -->
        <div class="output-card">
          <div class="output-card-label">AD ID</div>

          <!-- Editing -->
          <template v-if="adId.editing">
            <div class="output-field-row output-field-row--top">
              <div class="output-edit-col">
                <input
                  v-model="adId.draft"
                  class="output-inline-input output-mono"
                  placeholder="Paste ad link or enter ad ID"
                  @keyup.enter="saveAdId"
                  @keyup.esc="cancelAdId"
                />
                <span class="output-char-source">Paste the full Facebook ad link or type the ad ID directly.</span>
              </div>
              <div class="output-field-actions output-field-actions--top">
                <button class="output-icon-btn output-icon-btn--confirm" :disabled="adId.saving" @click="saveAdId">
                  <i :class="adId.saving ? 'fa fa-spinner fa-spin' : 'fa fa-check'"></i>
                </button>
                <button class="output-icon-btn" @click="cancelAdId"><i class="fa fa-times"></i></button>
              </div>
            </div>
            <div v-if="adId.error" class="output-validation-error"><i class="fa fa-exclamation-circle"></i> {{ adId.error }}</div>
          </template>

          <!-- Ad ID attached -->
          <template v-else-if="adId.value">
            <div class="output-field-row">
              <span class="output-field-value output-mono">{{ adId.value }}</span>
              <div class="output-field-actions">
                <button class="output-icon-btn" :class="{ 'output-icon-btn--copied': copiedKey === 'ad_id' }" @click="copy(adId.value, 'ad_id', null, 'ad_id')">
                  <i :class="copiedKey === 'ad_id' ? 'fa fa-check' : 'fa fa-copy'"></i>
                  <span v-if="copiedKey === 'ad_id'" class="output-copied-label">Copied!</span>
                </button>
              </div>
            </div>
            <div class="output-ad-attached"><i class="fa fa-check-circle"></i> Attached successfully</div>

            <!-- Ad details lookup -->
            <div v-if="adDetails.loading" class="output-loading"><i class="fa fa-spinner fa-spin"></i> Looking up ad details…</div>
            <div v-else-if="adDetails.verified" class="output-ad-details">
              <div class="output-ad-detail-row"><span class="output-ad-detail-label">Ad</span><span class="output-ad-detail-value">{{ adDetails.ad_name }}</span></div>
              <div class="output-ad-detail-row"><span class="output-ad-detail-label">Ad set</span><span class="output-ad-detail-value">{{ adDetails.adset_name }}</span></div>
              <div class="output-ad-detail-row"><span class="output-ad-detail-label">Campaign</span><span class="output-ad-detail-value">{{ adDetails.campaign_name }}</span></div>
            </div>
            <div v-else-if="adDetails.checked" class="output-ad-unverified">
              <i class="fa fa-exclamation-triangle"></i> I am unable to verify this ad ID.
            </div>

            <button class="output-click-edit" @click="startEditAdId">Edit</button>
            <span v-if="adId.saved" class="output-meta-saved-msg"><i class="fa fa-check-circle"></i> Saved</span>
          </template>

          <!-- No ad ID -->
          <template v-else>
            <p class="output-ad-hint">Attach an ad ID to route users who arrive from this ad with a generic message (e.g. "hi") to this entry point's journey and welcome message.</p>
            <button class="output-ad-attach-btn" @click="startEditAdId"><i class="fa fa-plus"></i> Attach ad ID</button>
          </template>
        </div>

        <!-- ── WELCOME MESSAGE ── -->
        <div class="output-card">
          <div class="output-card-label">WELCOME MESSAGE</div>
          <div v-for="(msg, lang) in drafts.welcomeMessages" :key="lang" class="output-welcome-block">
            <div class="output-lang-label">{{ lang }}</div>
            <div class="output-field-row output-field-row--top">
              <span v-if="!editing.welcomeMessages[lang]" class="output-field-value output-welcome-text">{{ msg }}</span>
              <div v-else class="output-edit-col">
                <textarea v-model="drafts.welcomeMessages[lang]" class="output-inline-textarea" rows="3" @keyup.esc="cancelWelcome(lang)"></textarea>
                <div class="output-char-row">
                  <span class="output-char-counter" :class="{ 'output-char-over': isWelcomeOver(lang) }">
                    {{ (drafts.welcomeMessages[lang] || '').length }}/{{ welcomeCharLimit }}
                  </span>
                  <span v-if="welcomeLimitingChannel" class="output-char-source">{{ welcomeLimitingChannel }}</span>
                </div>
              </div>
              <div class="output-field-actions output-field-actions--top">
                <template v-if="editing.welcomeMessages[lang]">
                  <button class="output-icon-btn output-icon-btn--confirm" :disabled="isWelcomeOver(lang)" @click="saveWelcome(lang)"><i class="fa fa-check"></i></button>
                  <button class="output-icon-btn" @click="cancelWelcome(lang)"><i class="fa fa-times"></i></button>
                </template>
                <button class="output-icon-btn" :class="{ 'output-icon-btn--copied': copiedKey === `welcome_${lang}` }" @click="copy(msg, 'welcome_message', null, `welcome_${lang}`)">
                  <i :class="copiedKey === `welcome_${lang}` ? 'fa fa-check' : 'fa fa-copy'"></i>
                  <span v-if="copiedKey === `welcome_${lang}`" class="output-copied-label">Copied!</span>
                </button>
              </div>
            </div>
            <button v-if="!editing.welcomeMessages[lang]" class="output-click-edit" @click="startEditWelcome(lang)">Click to edit</button>
          </div>
        </div>

        <!-- ── PHRASE + DEEP LINKS + QR per phrase ── -->
        <div v-for="(phrase, idx) in drafts.phrases" :key="idx" class="output-card output-phrase-card">

          <!-- Phrase header -->
          <div class="output-card-label">PHRASE {{ drafts.phrases.length > 1 ? idx + 1 : '' }}</div>
          <div class="output-field-row output-field-row--top">
            <span v-if="!editing.phrases[idx]" class="output-field-value output-mono">{{ phrase }}</span>
            <div v-else class="output-edit-col">
              <input
                v-model="drafts.phrases[idx]"
                class="output-inline-input output-mono"
                @keyup.esc="cancelPhrase(idx)"
              />
              <div class="output-char-row">
                <span class="output-char-counter" :class="{ 'output-char-over': isPhraseOver(idx) }">
                  {{ drafts.phrases[idx].length }}/{{ phraseCharLimit }}
                </span>
                <span v-if="limitingChannel" class="output-char-source">{{ limitingChannel }}</span>
              </div>
              <div v-if="interfaces.includes('messenger') && isMessengerPhraseInvalid(idx)" class="output-validation-error">
                <i class="fa fa-exclamation-circle"></i> Messenger: only letters, numbers, hyphens and underscores allowed (no spaces)
              </div>
            </div>
            <div class="output-field-actions">
              <template v-if="editing.phrases[idx]">
                <button class="output-icon-btn output-icon-btn--confirm" :disabled="isPhraseOver(idx) || validating.phrases[idx] || (interfaces.includes('messenger') && isMessengerPhraseInvalid(idx))" @click="savePhrase(idx)">
                  <i :class="validating.phrases[idx] ? 'fa fa-spinner fa-spin' : 'fa fa-check'"></i>
                </button>
                <button class="output-icon-btn" @click="cancelPhrase(idx)"><i class="fa fa-times"></i></button>
              </template>
              <button class="output-icon-btn" :class="{ 'output-icon-btn--copied': copiedKey === `phrase_${idx}` }" @click="copy(phrase, 'phrase', null, `phrase_${idx}`)">
                <i :class="copiedKey === `phrase_${idx}` ? 'fa fa-check' : 'fa fa-copy'"></i>
                <span v-if="copiedKey === `phrase_${idx}`" class="output-copied-label">Copied!</span>
              </button>
            </div>
          </div>
          <div v-if="errors.phrases[idx]" class="output-validation-error"><i class="fa fa-exclamation-circle"></i> {{ errors.phrases[idx] }}</div>
          <button v-if="!editing.phrases[idx]" class="output-click-edit" @click="startEditPhrase(idx)">Click to edit</button>

          <!-- Per-channel blocks: deep link + short URL + both QR codes -->
          <div v-if="configLoading" class="output-loading"><i class="fa fa-spinner fa-spin"></i> Loading config…</div>
          <template v-else-if="interfaces.length">
            <div v-for="ch in interfaces" :key="`ch_${idx}_${ch}`" class="output-channel-block">

              <div class="output-divider"></div>
              <div class="output-channel-block-header">
                <span class="output-channel-badge" :class="`output-ch-${ch}`">{{ channelLabel(ch) }}</span>
              </div>

              <div v-if="!deepLinks[idx] || !deepLinks[idx][ch]" class="output-no-config">
                <i class="fa fa-exclamation-circle"></i> No configuration found
              </div>
              <template v-else>

              <!-- Deep Link -->
              <div class="output-sub-label output-sub-label--mt">DEEP LINK</div>
              <div class="output-field-row output-dl-row">
                <span class="output-field-value output-mono output-break-all">{{ deepLinks[idx][ch] }}</span>
                <button class="output-icon-btn" :class="{ 'output-icon-btn--copied': copiedKey === `dl_${idx}_${ch}` }" @click="copy(deepLinks[idx][ch], 'deep_link', ch, `dl_${idx}_${ch}`)">
                  <i :class="copiedKey === `dl_${idx}_${ch}` ? 'fa fa-check' : 'fa fa-copy'"></i>
                  <span v-if="copiedKey === `dl_${idx}_${ch}`" class="output-copied-label">Copied!</span>
                </button>
              </div>

              <!-- Short URL -->
              <div class="output-sub-label output-sub-label--mt">SHORT URL</div>
              <div class="output-field-row">
                <span class="output-field-value output-mono output-break-all">{{ getShortUrl(idx, ch) }}</span>
                <button class="output-icon-btn" :class="{ 'output-icon-btn--copied': copiedKey === `su_${idx}_${ch}` }" @click="copy(getShortUrl(idx, ch), 'short_url', ch, `su_${idx}_${ch}`)">
                  <i :class="copiedKey === `su_${idx}_${ch}` ? 'fa fa-check' : 'fa fa-copy'"></i>
                  <span v-if="copiedKey === `su_${idx}_${ch}`" class="output-copied-label">Copied!</span>
                </button>
              </div>
              <label class="output-checkbox-label">
                <input type="checkbox" :checked="shortUrlState[idx] && shortUrlState[idx][ch] && shortUrlState[idx][ch].useCustom" @change="toggleCustomUrl(idx, ch)" />
                <span>Use custom URL</span>
              </label>
              <template v-if="shortUrlState[idx] && shortUrlState[idx][ch] && shortUrlState[idx][ch].useCustom">
                <div class="output-custom-url-row">
                  <span class="output-url-prefix">{{ urlPrefix }}</span>
                  <input :value="shortUrlState[idx][ch].customSlug" class="output-slug-input" :class="{ 'output-slug-input--error': slugError(idx, ch) }" @input="e => onSlugInput(idx, ch, e.target.value)" />
                </div>
                <div v-if="slugError(idx, ch)" class="output-validation-error"><i class="fa fa-exclamation-circle"></i> {{ slugError(idx, ch) }}</div>
                <transition name="warn-fade">
                  <div v-if="shortUrlState[idx][ch].showWarning" class="output-url-warning">
                    <i class="fa fa-exclamation-triangle output-warn-icon"></i>
                    <span>Changing the short URL will also update the QR code. The old URL will stop working.</span>
                    <div class="output-warn-actions">
                      <button class="output-warn-cancel-btn" @click="cancelShortUrl(idx, ch)">Cancel</button>
                      <button class="output-warn-confirm-btn" @click="confirmShortUrl(idx, ch)">Confirm change</button>
                    </div>
                  </div>
                </transition>
                <div
                  v-if="!shortUrlState[idx][ch].showWarning && shortUrlState[idx][ch].customSlug.trim() && !slugError(idx, ch) && shortUrlState[idx][ch].customSlug !== shortUrlState[idx][ch].currentSlug"
                  class="output-apply-row"
                >
                  <button class="output-apply-btn" @click="applyShortUrl(idx, ch)">Apply</button>
                  <span class="output-apply-hint">Click to generate the short link and regenerate its QR code</span>
                </div>
                <span v-if="shortUrlState[idx][ch].justSaved" class="output-meta-saved-msg"><i class="fa fa-check-circle"></i> Saved</span>
              </template>

              <!-- QR codes: deep link + short URL side by side -->
              <div class="output-sub-label output-sub-label--mt">QR CODES</div>
              <div class="output-qr-pair">
                <div class="output-qr-block">
                  <div class="output-qr-block-label">Deep link</div>
                  <div class="output-qr-img-wrap">
                    <img v-if="qrCodes[idx] && qrCodes[idx][ch] && qrCodes[idx][ch].preview" :src="qrCodes[idx][ch].preview" class="output-qr-img" :alt="`QR ${channelLabel(ch)} deep`" />
                    <div v-else class="output-qr-placeholder"><i class="fa fa-qrcode output-qr-placeholder-icon"></i></div>
                  </div>
                  <button class="output-download-btn output-download-btn--sm" @click="downloadQr(idx, ch)"><i class="fa fa-download"></i> Download</button>
                </div>
                <div class="output-qr-block">
                  <div class="output-qr-block-label">Short URL</div>
                  <div class="output-qr-img-wrap">
                    <img v-if="shortQrCodes[idx] && shortQrCodes[idx][ch] && shortQrCodes[idx][ch].preview" :src="shortQrCodes[idx][ch].preview" class="output-qr-img" :alt="`QR ${channelLabel(ch)} short`" />
                    <div v-else class="output-qr-placeholder"><i class="fa fa-qrcode output-qr-placeholder-icon"></i></div>
                  </div>
                  <button class="output-download-btn output-download-btn--sm" @click="downloadShortQr(idx, ch)"><i class="fa fa-download"></i> Download</button>
                </div>
              </div>
              <label class="output-toggle-row" @click.prevent="toggleNiviLogo(idx, ch)">
                <span class="output-toggle-label-sm">Include Nivi logo (both QRs)</span>
                <div class="output-toggle" :class="{ 'output-toggle--on': niviLogoState[idx] && niviLogoState[idx][ch] }">
                  <span class="output-toggle-knob"></span>
                </div>
              </label>

              </template>
            </div><!-- /channel block -->
          </template>
          <div v-else class="output-loading">No channels configured (interface missing in additional_details)</div>

        </div><!-- /phrase card -->

        <!-- ── CAMPAIGN METADATA ── -->
        <div class="output-card output-meta-form-card">
          <div class="output-card-label">KEYWORD METADATA</div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">Keyword Use</label>
            <nv-select v-model="metaDrafts.keyword_usage" class="output-meta-select">
              <option value="">-- Choose --</option>
              <option v-for="(item, i) in keywordUseList" :key="i" :value="item.usage_type">{{ item.labels }}</option>
            </nv-select>
          </div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">Keyword Topic</label>
            <nv-select v-model="metaDrafts.topic" class="output-meta-select">
              <option value="">-- Choose --</option>
              <option v-for="(item, i) in topicList" :key="i" :value="item.display_name">{{ item.display_name }}</option>
            </nv-select>
          </div>

          <div class="output-card-label output-card-label--mt">CAMPAIGN INFORMATION</div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">Campaign</label>
            <nv-select v-model="metaDrafts.campaign_name" class="output-meta-select">
              <option value="">-- Choose a Campaign --</option>
              <option v-for="(item, i) in campaignList" :key="i" :value="item.name">{{ item.name }}</option>
            </nv-select>
          </div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">Campaign Code</label>
            <div class="output-meta-input-row">
              <input v-model="metaDrafts.campaignCode" class="output-meta-input" placeholder="Campaign Code" />
              <button class="output-meta-gen-btn" :disabled="!kw || !kw.client_id" @click="generateMetaCampaignCode" title="Generate code">
                <i class="fa fa-refresh"></i>
              </button>
            </div>
          </div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">UTM Source</label>
            <input v-model="metaDrafts.utm_source" class="output-meta-input" placeholder="UTM Source" />
          </div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">UTM Medium</label>
            <input v-model="metaDrafts.utm_medium" class="output-meta-input" placeholder="UTM Medium" />
          </div>

          <div class="output-meta-form-row">
            <label class="output-meta-label">UTM Campaign</label>
            <input v-model="metaDrafts.utm_campaign" class="output-meta-input" placeholder="UTM Campaign" />
          </div>

          <div class="output-meta-actions">
            <button class="output-meta-save-btn" :disabled="metaSaving" @click="saveMetadata">
              <i :class="metaSaving ? 'fa fa-spinner fa-spin' : 'fa fa-check'"></i>
              {{ metaSaving ? 'Saving…' : 'Save Metadata' }}
            </button>
            <span v-if="metaSaved" class="output-meta-saved-msg"><i class="fa fa-check-circle"></i> Saved</span>
          </div>
        </div>

      </div><!-- /body -->
    </template>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import api from '../../../../../utils/api'
import { domain } from '../../../../../config/constants'

const CHANNEL_LIMITS = { whatsapp: 1024, messenger: 640, line: 2000 }
const WELCOME_LIMITS = { whatsapp: 1024, messenger: 2000, line: 2000 }
const CHANNEL_LABELS = { whatsapp: 'WhatsApp', messenger: 'Messenger', line: 'LINE' }
const LANG_NAMES = { eng: 'English', hi: 'Hindi', swa: 'Swahili', hau: 'Hausa', mar: 'Marathi', guj: 'Gujarati', tam: 'Tamil', odi: 'Odia', asm: 'Assamese', kan: 'Kannada', tel: 'Telugu', tha: 'Thai', ind: 'Indonesian' }

export default {
  name: 'OutputSidebar',

  /**
   * output prop shape:
   * {
   *   keywordObj: {          ← full keyword object from DB/API
   *     id: Number,
   *     keyword: String,
   *     data: {
   *       country: String,
   *       utm_source, utm_medium, utm_campaign, campaignCode, keyword_usage, topic,
   *       additional_details: {
   *         interface: String[],          ← ['whatsapp','messenger','line']
   *         welcomeMessages: { [lang]: String }
   *       }
   *     }
   *   },
   *   phrases: String[]      ← array of phrase strings
   * }
   */
  props: {
    output: { type: Object, default: null },
    showReadyHeader: { type: Boolean, default: true }
  },

  data () {
    return {
      // Quick-link config from API: { whatsapp: { Kenya: '254...' }, messenger: {...} }
      config: {},
      configLoading: false,
      // Editable drafts
      drafts: { keyword: '', phrases: [], welcomeMessages: {} },
      originals: { keyword: '', phrases: [], welcomeMessages: {} },
      editing: { keyword: false, phrases: {}, welcomeMessages: {} },
      // deepLinks[phraseIdx][channel] = url
      deepLinks: {},
      // qrCodes[phraseIdx][channel] = { preview: dataUrl, download: dataUrl }
      qrCodes: {},
      // shortQrCodes[phraseIdx][channel] = { preview: dataUrl, download: dataUrl }
      shortQrCodes: {},
      // niviLogoState[phraseIdx][channel] = bool
      niviLogoState: {},
      // Copy feedback
      copiedKey: null,
      copiedTimer: null,
      // shortUrlState[phraseIdx][channel] = { currentSlug, customSlug, useCustom, showWarning }
      shortUrlState: {},
      phraseObjects: [],        // raw phrase objects from API (needed for id on save)
      langCodesMap: {},         // { 'English': 'eng', 'Hindi': 'hi', ... } for customConfig rebuild
      originalCustomConfig: {}, // deep copy of parsed customConfig for rebuild
      errors: { keyword: '', phrases: {} },
      validating: { keyword: false, phrases: {} },
      // Metadata form
      keywordUseList: [],
      topicList: [],
      campaignList: [],
      metaDrafts: { keyword_usage: '', topic: '', campaign_name: '', campaignCode: '', utm_source: '', utm_medium: '', utm_campaign: '' },
      metaSaving: false,
      metaSaved: false,
      metaSavedTimer: null,
      // Ad ID (Flow A) — stored in additional_details.ad_details.source_id
      adId: { value: '', draft: '', editing: false, saving: false, saved: false, error: '' },
      adIdSavedTimer: null,
      adDetails: { loading: false, checked: false, verified: false, ad_name: '', adset_name: '', campaign_name: '' }
    }
  },

  computed: {
    // Flat keyword record (output.keyword mirrors the DB row directly)
    kw () { return this.output && this.output.keyword },

    additionalDetails () {
      const raw = this.kw && this.kw.additional_details
      if (!raw) return {}
      try { return typeof raw === 'string' ? JSON.parse(raw) : raw } catch (e) { return {} }
    },

    interfaces () { return (this.additionalDetails.interface || []).map(i => i.toLowerCase()) },
    country () { return (this.kw && this.kw.country) || '' },

    phraseCharLimit () {
      const limits = this.interfaces.map(ch => CHANNEL_LIMITS[ch]).filter(Boolean)
      return limits.length ? Math.min(...limits) : 1024
    },
    limitingChannel () {
      let min = Infinity; let ch = ''
      this.interfaces.forEach(c => {
        if (CHANNEL_LIMITS[c] && CHANNEL_LIMITS[c] < min) { min = CHANNEL_LIMITS[c]; ch = CHANNEL_LABELS[c] || c }
      })
      return ch
    },
    welcomeCharLimit () {
      const limits = this.interfaces.map(ch => WELCOME_LIMITS[ch]).filter(Boolean)
      return limits.length ? Math.min(...limits) : 1024
    },
    welcomeLimitingChannel () {
      let min = Infinity; let ch = ''
      this.interfaces.forEach(c => {
        if (WELCOME_LIMITS[c] && WELCOME_LIMITS[c] < min) { min = WELCOME_LIMITS[c]; ch = CHANNEL_LABELS[c] || c }
      })
      return ch
    },

    urlPrefix () {
      const hostname = window.location.hostname
      return (domain[hostname] && domain[hostname].clickUrlDomain) || 'https://xs.nivi.io/'
    }
  },

  watch: {
    output: {
      immediate: true,
      async handler (val) {
        if (!val || !val.keyword) return

        const kw = val.keyword

        // Parse additional_details JSON string
        let ad = {}
        try { ad = kw.additional_details ? (typeof kw.additional_details === 'string' ? JSON.parse(kw.additional_details) : kw.additional_details) : {} } catch (e) {}

        // Extract phrase strings from phrase objects array
        const phrases = (val.phrase || []).map(p => p.phrase)

        // Extract welcome messages from customConfig[langCode].sms_welcome1
        const welcomeMessages = {}
        if (kw.customMessages === 'yes' && kw.customConfig) {
          try {
            const cfg = typeof kw.customConfig === 'string' ? JSON.parse(kw.customConfig) : kw.customConfig
            Object.entries(cfg).forEach(([langCode, msgs]) => {
              const msg = msgs && msgs.sms_welcome1
              if (msg && msg.trim()) {
                welcomeMessages[LANG_NAMES[langCode] || langCode] = msg.trim()
              }
            })
          } catch (e) {}
        }

        const interfaces = (ad.interface || []).map(i => i.toLowerCase())
        const baseSlug = (kw.keyword || '').toLowerCase().replace(/_/g, '-')
        const chCode = { whatsapp: 'wa', messenger: 'ms', line: 'ln' }

        // Hydrate ad id from additional_details.ad_details.source_id
        const existingAdId = (ad.ad_details && ad.ad_details.source_id) || ''
        this.adId = { value: existingAdId, draft: existingAdId, editing: false, saving: false, saved: false, error: '' }
        this.adDetails = { loading: false, checked: false, verified: false, ad_name: '', adset_name: '', campaign_name: '' }
        if (existingAdId) this.fetchAdDetails(existingAdId)

        this.drafts = { keyword: kw.keyword || '', phrases, welcomeMessages }
        this.originals = { keyword: kw.keyword || '', phrases: phrases.slice(), welcomeMessages: { ...welcomeMessages } }
        this.editing = { keyword: false, phrases: {}, welcomeMessages: {} }
        this.deepLinks = {}
        this.qrCodes = {}
        this.shortQrCodes = {}
        this.niviLogoState = {}
        // Populate metadata drafts from keyword
        this.metaDrafts = {
          keyword_usage: kw.keyword_usage || '',
          topic: kw.topic || '',
          campaign_name: kw.campaign || '',
          campaignCode: kw.campaignCode || '',
          utm_source: kw.utm_source || '',
          utm_medium: kw.utm_medium || '',
          utm_campaign: kw.utm_campaign || ''
        }

        // Init per-phrase-per-channel short URL state
        const chAbbr = { whatsapp: 'WA', messenger: 'MS', line: 'LN' }
        const existingLinks = val.existingLinks || []
        const suState = {}
        phrases.forEach((_, idx) => {
          suState[idx] = {}
          const phraseId = (val.phrase || [])[idx] && (val.phrase || [])[idx].id
          interfaces.forEach(ch => {
            const suffix = phrases.length > 1 ? `-p${idx + 1}` : ''
            const autoSlug = `${baseSlug}-${chCode[ch] || ch}${suffix}`
            const abbr = chAbbr[ch] || ch.toUpperCase()
            const existing = existingLinks.find(l => String(l.context_id) === String(phraseId) && l.name && l.name.endsWith(`- ${abbr}`))
            suState[idx][ch] = {
              currentSlug: existing ? existing.code : autoSlug,
              customSlug: existing ? existing.code : autoSlug,
              // There's no persisted "is this a custom slug" flag — only a single code
              // column — so infer it deterministically: if the saved slug doesn't match
              // what the auto-generation formula would produce, it must have been
              // hand-edited, and the checkbox should reflect that on every reload.
              useCustom: !!(existing && existing.code !== autoSlug),
              showWarning: false,
              trackedUrlId: existing ? existing.id : null
            }
          })
        })
        this.shortUrlState = suState

        // Store phrase objects for API calls
        this.phraseObjects = (val.phrase || [])

        // Store lang code reverse map + original customConfig for welcome message API
        this.langCodesMap = {}
        this.originalCustomConfig = {}
        if (kw.customConfig) {
          try {
            const cfg = typeof kw.customConfig === 'string' ? JSON.parse(kw.customConfig) : kw.customConfig
            this.originalCustomConfig = JSON.parse(JSON.stringify(cfg))
            Object.entries(cfg).forEach(([langCode]) => {
              const displayName = LANG_NAMES[langCode] || langCode
              this.langCodesMap[displayName] = langCode
            })
          } catch (e) {}
        }

        await this.fetchConfig()
      }
    }
  },

  methods: {
    channelLabel (ch) { return CHANNEL_LABELS[ch] || ch },

    async stampUpdatedBy () {
      const epId = this.output && this.output.entry_point_id
      if (!epId) return
      const updatedBy = localStorage.getItem('email') || ''
      try {
        await api.buildRequest(`entry-points/${epId}`, 'PUT', { updated_by: updatedBy })
      } catch (err) {
        console.error('[OutputSidebar] stampUpdatedBy failed', err)
      }
    },

    // ── Config + deep link generation ──
    async fetchConfig () {
      this.configLoading = true
      try {
        const { data } = await api.buildRequest('getQuickLinkConfig', 'POST')
        if (data && data.payload) {
          this.config = data.payload
          this.rebuildAllDeepLinks()
          await this.generateAllQRs()
        }
      } catch (err) {
        console.error('Failed to fetch quick link config', err)
      } finally {
        this.configLoading = false
      }
    },

    getMappedId (channel) {
      const platformConfig = this.config[channel] || {}
      const val = platformConfig[this.country]
      if (!val) return ''
      return typeof val === 'string' ? val : (val.id || '')
    },

    buildDeepLink (channel, phrase) {
      const mappedId = this.getMappedId(channel)
      if (!mappedId) return ''
      const encoded = encodeURIComponent(phrase)
      if (channel === 'whatsapp') return `https://wa.me/${mappedId}?text=${encoded}`
      if (channel === 'messenger') return `https://m.me/${mappedId}?text=${encoded}`
      if (channel === 'line') return `https://line.me/R/oaMessage/${mappedId}/?${encoded}`
      return ''
    },

    rebuildAllDeepLinks () {
      const newLinks = {}
      this.drafts.phrases.forEach((phrase, idx) => {
        newLinks[idx] = {}
        this.interfaces.forEach(ch => {
          newLinks[idx][ch] = this.buildDeepLink(ch, phrase)
        })
      })
      this.deepLinks = newLinks
    },

    async _genQrPair (url, withLogo) {
      let [preview, download] = await Promise.all([
        QRCode.toDataURL(url, { errorCorrectionLevel: 'H', margin: 1, width: 256 }),
        QRCode.toDataURL(url, { errorCorrectionLevel: 'H', margin: 1, width: 1024 })
      ])
      if (withLogo) {
        ;[preview, download] = await Promise.all([
          this.compositeLogoOnQr(preview, 256),
          this.compositeLogoOnQr(download, 1024)
        ])
      }
      return { preview, download }
    },

    async generateAllQRs () {
      const newQrs = {}
      const newShortQrs = {}
      for (let idx = 0; idx < this.drafts.phrases.length; idx++) {
        newQrs[idx] = {}
        newShortQrs[idx] = {}
        for (const ch of this.interfaces) {
          const withLogo = (this.niviLogoState[idx] && this.niviLogoState[idx][ch]) || false
          const deepLink = this.deepLinks[idx] && this.deepLinks[idx][ch]
          const shortLink = this.getShortUrl(idx, ch)
          try {
            if (deepLink) newQrs[idx][ch] = await this._genQrPair(deepLink, withLogo)
          } catch (e) { console.error('QR gen failed (deep)', idx, ch, e) }
          try {
            if (shortLink) newShortQrs[idx][ch] = await this._genQrPair(shortLink, withLogo)
          } catch (e) { console.error('QR gen failed (short)', idx, ch, e) }
        }
      }
      this.qrCodes = newQrs
      this.shortQrCodes = newShortQrs
    },

    async regenerateQRForPhrase (phraseIdx) {
      const newQrs = { ...(this.qrCodes[phraseIdx] || {}) }
      const newShortQrs = { ...(this.shortQrCodes[phraseIdx] || {}) }
      for (const ch of this.interfaces) {
        const withLogo = (this.niviLogoState[phraseIdx] && this.niviLogoState[phraseIdx][ch]) || false
        const deepLink = this.deepLinks[phraseIdx] && this.deepLinks[phraseIdx][ch]
        const shortLink = this.getShortUrl(phraseIdx, ch)
        try {
          if (deepLink) newQrs[ch] = await this._genQrPair(deepLink, withLogo)
        } catch (e) {}
        try {
          if (shortLink) newShortQrs[ch] = await this._genQrPair(shortLink, withLogo)
        } catch (e) {}
      }
      this.$set(this.qrCodes, phraseIdx, newQrs)
      this.$set(this.shortQrCodes, phraseIdx, newShortQrs)
    },

    // ── Build updateKeyword payload from current kw data ──
    buildKeywordPayload (overrides = {}) {
      const kw = this.kw
      return {
        updatedRow: {
          id: String(kw.id),
          keyword: overrides.keyword || kw.keyword,
          oldKeywordText: kw.keyword,
          oldCountryText: kw.country,
          data: {
            ...kw,
            keyword: overrides.keyword || kw.keyword,
            customConfig: overrides.customConfig !== undefined ? overrides.customConfig : kw.customConfig,
            country: kw.country,
            // camelCase mappings expected by updateKeyword
            clientId: kw.client_id,
            conversationId: kw.conversation_id,
            topicId: kw.topic_id,
            isQRCode: kw.is_QR_code_printed,
            referralResource: kw.referral_resource_link,
            qrCodePlacement: kw.qr_code_placement,
            setPreferredUserLanguage: kw.set_preferred_user_language,
            setPreferredUserGender: kw.set_preferred_user_gender || 'ASK_THE_USER',
            setEnableNlpOnboarding: kw.enable_nlp_onboarding,
            setAdditionalDetails: kw.additional_details,
            conv_type: kw.appPackage ? kw.appPackage.toUpperCase() : null
          }
        }
      }
    },

    // ── Validation ──
    slugError (idx, ch) {
      const s = this.shortUrlState[idx] && this.shortUrlState[idx][ch]
      if (!s || !s.useCustom) return ''
      if (s.duplicateError) return s.duplicateError
      const slug = s.customSlug
      if (!slug.trim()) return 'Slug cannot be empty'
      if (!/^[a-zA-Z0-9-]+$/.test(slug)) return 'Only letters, numbers and hyphens allowed'
      if (slug.length < 3) return 'Slug must be at least 3 characters'
      if (slug.length > 60) return 'Slug must be under 60 characters'
      return ''
    },

    async checkKeywordDuplicate (keyword) {
      const { data } = await api.buildRequest('/checkKeywordExists', 'POST', {
        selector: {
          keyword,
          'data.country': this.kw.country,
          isdeleted: false,
          id: this.kw.id
        }
      })
      return !!(data && data.payload && data.payload.exists)
    },

    async checkPhraseDuplicate (phrase, phraseId) {
      const { data } = await api.buildRequest('/checkPhraseExists', 'POST', {
        selector: {
          phrase,
          country_id: this.kw.country_id,
          is_deleted: false,
          id: phraseId
        }
      })
      return !!(data && data.payload && data.payload.exists)
    },

    // ── Keyword ──
    startEdit (field) {
      this.$set(this.errors, 'keyword', '')
      this.$set(this.editing, field, true)
    },
    async saveField (field) {
      if (!this.drafts[field].trim()) return
      if (field === 'keyword') {
        const newVal = this.drafts.keyword.trim()
        if (newVal === this.originals.keyword) {
          this.$set(this.editing, field, false)
          return
        }
        this.$set(this.validating, 'keyword', true)
        this.$set(this.errors, 'keyword', '')
        try {
          const isDuplicate = await this.checkKeywordDuplicate(newVal)
          if (isDuplicate) {
            this.$set(this.errors, 'keyword', 'A keyword with this name already exists in this country')
            return
          }
        } catch (err) {
          console.error('Keyword duplicate check failed', err)
        } finally {
          this.$set(this.validating, 'keyword', false)
        }
        this.$set(this.editing, field, false)
        try {
          await api.buildRequest('/updateKeyword', 'POST', this.buildKeywordPayload({ keyword: newVal }))
          this.originals.keyword = newVal
          await this.updateSlugsForKeywordChange(newVal)
          this.stampUpdatedBy()
          this.$emit('output_panel_edit', { entry_point_id: this.kw && this.kw.id, field_type: 'keyword', timestamp: Date.now() })
        } catch (err) {
          console.error('Failed to update keyword', err)
        }
      }
    },
    cancelField (field) {
      this.drafts[field] = this.originals[field]
      this.$set(this.errors, 'keyword', '')
      this.$set(this.editing, field, false)
    },

    // ── Ad ID (Flow A) ──
    // Accept either a raw ad ID or a full Facebook Ads Manager link and
    // extract the ad ID from its `selected_ad_ids` query param.
    // Returns { id, error } — error is set when zero or more-than-one ad id
    // is found (e.g. multiple ads checked in Ads Manager before copying the link).
    parseAdId (raw) {
      const input = (raw || '').trim()
      if (!input) return { id: '', error: 'Enter an ad ID or paste a Facebook ad link.' }
      if (/^\d+$/.test(input)) return { id: input, error: '' }

      let selectedAdIds = ''
      try {
        const url = new URL(input)
        selectedAdIds = url.searchParams.get('selected_ad_ids') || ''
      } catch (e) {
        const m = input.match(/selected_ad_ids=([^&\s]+)/)
        if (m) selectedAdIds = decodeURIComponent(m[1])
      }

      const ids = selectedAdIds.split(',').map(s => s.trim()).filter(Boolean)
      if (!ids.length) return { id: '', error: 'No ad ID found in this link. Please paste a link with a single ad selected, or enter the ad ID directly.' }
      if (ids.length > 1) return { id: '', error: 'Multiple ads found in this link. Please select a single ad in Ads Manager before copying the link, or enter the ad ID directly.' }
      return { id: ids[0], error: '' }
    },

    startEditAdId () {
      this.adId.draft = this.adId.value
      this.adId.error = ''
      this.adId.editing = true
    },
    cancelAdId () {
      this.adId.draft = this.adId.value
      this.adId.error = ''
      this.adId.editing = false
    },
    async saveAdId () {
      if (!this.kw) return
      const { id: parsed, error } = this.parseAdId(this.adId.draft)
      if (!parsed) {
        this.adId.error = error || 'Enter a valid ad ID or a Facebook ad link containing selected_ad_ids.'
        return
      }
      if (parsed === this.adId.value) {
        this.adId.editing = false
        return
      }
      this.adId.saving = true
      this.adId.error = ''
      try {
        const merged = { ...this.additionalDetails, ad_details: { ...(this.additionalDetails.ad_details || {}), source_id: parsed } }
        const payload = this.buildKeywordPayload({})
        payload.updatedRow.data.setAdditionalDetails = merged
        await api.buildRequest('/updateKeyword', 'POST', payload)
        this.adId.value = parsed
        this.adId.draft = parsed
        this.adId.editing = false
        this.stampUpdatedBy()
        this.$emit('output_panel_edit', { entry_point_id: this.kw && this.kw.id, field_type: 'ad_id', timestamp: Date.now() })
        clearTimeout(this.adIdSavedTimer)
        this.adId.saved = true
        this.adIdSavedTimer = setTimeout(() => { this.adId.saved = false }, 3000)
        this.fetchAdDetails(parsed)
      } catch (err) {
        console.error('[OutputSidebar] saveAdId failed', err)
        this.adId.error = 'Failed to save ad ID. Please try again.'
      } finally {
        this.adId.saving = false
      }
    },
    async fetchAdDetails (adId) {
      console.log('[OutputSidebar] fetchAdDetails: start', adId)
      this.adDetails = { loading: true, checked: false, verified: false, ad_name: '', adset_name: '', campaign_name: '' }
      try {
        const { data } = await api.buildRequest(`ad-details/${adId}`, 'GET')
        console.log('[OutputSidebar] fetchAdDetails: response', data)
        const payload = data && data.payload
        if (payload && payload.verified) {
          this.adDetails = { loading: false, checked: true, verified: true, ad_name: payload.ad_name, adset_name: payload.adset_name, campaign_name: payload.campaign_name }
          console.log('[OutputSidebar] fetchAdDetails: verified', this.adDetails)
        } else {
          this.adDetails = { loading: false, checked: true, verified: false, ad_name: '', adset_name: '', campaign_name: '' }
          console.log('[OutputSidebar] fetchAdDetails: unverified', payload && payload.message)
        }
      } catch (err) {
        console.error('[OutputSidebar] fetchAdDetails failed', adId, err)
        this.adDetails = { loading: false, checked: true, verified: false, ad_name: '', adset_name: '', campaign_name: '' }
      }
    },

    // ── Phrases ──
    startEditPhrase (idx) {
      this.$set(this.errors.phrases, idx, '')
      this.$set(this.editing.phrases, idx, true)
    },
    isPhraseOver (idx) { return (this.drafts.phrases[idx] || '').length > this.phraseCharLimit },
    isMessengerPhraseInvalid (idx) { return /[^a-zA-Z0-9\-_]/.test(this.drafts.phrases[idx] || '') },
    async savePhrase (idx) {
      if (this.isPhraseOver(idx)) return
      if (this.interfaces.includes('messenger') && this.isMessengerPhraseInvalid(idx)) return
      const newPhrase = this.drafts.phrases[idx]
      // Check duplicate within current phrases
      const dupeLocal = this.drafts.phrases.some((p, i) => i !== idx && p.trim() === newPhrase.trim())
      if (dupeLocal) {
        this.$set(this.errors.phrases, idx, 'This phrase is already used on this keyword')
        return
      }
      const phraseObj = this.phraseObjects[idx]
      this.$set(this.validating.phrases, idx, true)
      this.$set(this.errors.phrases, idx, '')
      try {
        const isDuplicate = await this.checkPhraseDuplicate(newPhrase, phraseObj && phraseObj.id)
        if (isDuplicate) {
          this.$set(this.errors.phrases, idx, 'This phrase already exists in this country')
          return
        }
      } catch (err) {
        console.error('Phrase duplicate check failed', err)
      } finally {
        this.$set(this.validating.phrases, idx, false)
      }
      this.$set(this.editing.phrases, idx, false)
      const newLinks = this.deepLinks[idx] || {}
      this.interfaces.forEach(ch => {
        newLinks[ch] = this.buildDeepLink(ch, newPhrase)
      })
      this.$set(this.deepLinks, idx, { ...newLinks })
      await this.regenerateQRForPhrase(idx)
      if (phraseObj && phraseObj.id) {
        try {
          await api.buildRequest('/updatePhrase', 'POST', {
            updatedRow: {
              id: phraseObj.id,
              phrase: newPhrase,
              keyword_id: phraseObj.keyword_id,
              country_id: phraseObj.country_id,
              phraseOldName: this.originals.phrases[idx],
              countryOldName: this.kw.country,
              keyword: this.kw.keyword,
              country_name: this.kw.country
            }
          })
          this.stampUpdatedBy()
          this.$emit('output_panel_edit', { entry_point_id: this.kw && this.kw.id, field_type: 'phrase', timestamp: Date.now() })
        } catch (err) {
          console.error('Failed to update phrase', err)
        }
      }
    },
    cancelPhrase (idx) {
      this.$set(this.drafts.phrases, idx, this.originals.phrases[idx])
      this.$set(this.errors.phrases, idx, '')
      this.$set(this.editing.phrases, idx, false)
    },

    // ── Welcome messages ──
    startEditWelcome (lang) { this.$set(this.editing.welcomeMessages, lang, true) },
    isWelcomeOver (lang) { return (this.drafts.welcomeMessages[lang] || '').length > this.welcomeCharLimit },
    async saveWelcome (lang) {
      if (this.isWelcomeOver(lang)) return
      this.$set(this.editing.welcomeMessages, lang, false)
      const langCode = this.langCodesMap[lang]
      if (langCode && this.originalCustomConfig[langCode] !== undefined) {
        const newConfig = JSON.parse(JSON.stringify(this.originalCustomConfig))
        newConfig[langCode].sms_welcome1 = this.drafts.welcomeMessages[lang]
        this.originalCustomConfig = newConfig
        try {
          await api.buildRequest('/updateKeyword', 'POST', this.buildKeywordPayload({ customConfig: newConfig }))
          this.stampUpdatedBy()
          this.$emit('output_panel_edit', { entry_point_id: this.kw && this.kw.id, field_type: 'welcome_message', timestamp: Date.now() })
        } catch (err) {
          console.error('Failed to update welcome message', err)
        }
      }
    },
    cancelWelcome (lang) {
      this.$set(this.drafts.welcomeMessages, lang, this.originals.welcomeMessages[lang])
      this.$set(this.editing.welcomeMessages, lang, false)
    },

    // ── Entry point link persistence ──
    async saveMissingEntryPointLinks () {
      const kw = this.kw
      if (!kw) return
      const chAbbr = { whatsapp: 'WA', messenger: 'MS', line: 'LN' }
      const promises = []
      this.drafts.phrases.forEach((_, idx) => {
        const phraseObj = this.phraseObjects[idx]
        if (!phraseObj) return
        this.interfaces.forEach(ch => {
          const s = this.shortUrlState[idx] && this.shortUrlState[idx][ch]
          if (!s || s.trackedUrlId) return
          const deepLink = this.deepLinks[idx] && this.deepLinks[idx][ch]
          if (!deepLink) return
          const abbr = chAbbr[ch] || ch.toUpperCase()
          const name = `Short URL - ${kw.keyword} - Phrase ${idx + 1} - ${abbr}`
          promises.push(
            api.buildRequest('saveEntryPointLink', 'POST', {
              name,
              code: s.currentSlug,
              long_url: deepLink,
              client_id: kw.client_id || null,
              topic_id: kw.topic_id || null,
              context_id: phraseObj.id
            }).then(res => {
              const saved = res.data && res.data.payload
              if (saved && saved.id) this._patchSu(idx, ch, { trackedUrlId: saved.id })
            }).catch(err => console.error('[OutputSidebar] saveMissingEntryPointLinks failed', idx, ch, err))
          )
        })
      })
      await Promise.all(promises)
    },

    _buildLinkPayload (idx, ch, slug, deepLink) {
      const kw = this.kw
      const phraseObj = this.phraseObjects[idx]
      const chAbbr = { whatsapp: 'WA', messenger: 'MS', line: 'LN' }
      const abbr = chAbbr[ch] || ch.toUpperCase()
      return {
        name: `Short URL - ${kw.keyword} - Phrase ${idx + 1} - ${abbr}`,
        code: slug,
        long_url: deepLink || '',
        client_id: kw.client_id || null,
        topic_id: kw.topic_id || null,
        context_id: phraseObj && phraseObj.id
      }
    },

    async updateSlugsForKeywordChange (newKeyword) {
      const chCode = { whatsapp: 'wa', messenger: 'ms', line: 'ln' }
      const baseSlug = newKeyword.toLowerCase().replace(/_/g, '-')
      const promises = []
      this.drafts.phrases.forEach((_, idx) => {
        this.interfaces.forEach(ch => {
          const s = this.shortUrlState[idx] && this.shortUrlState[idx][ch]
          if (!s || s.useCustom) return
          const suffix = this.drafts.phrases.length > 1 ? `-p${idx + 1}` : ''
          const newSlug = `${baseSlug}-${chCode[ch] || ch}${suffix}`
          this._patchSu(idx, ch, { currentSlug: newSlug, customSlug: newSlug })
          const deepLink = this.deepLinks[idx] && this.deepLinks[idx][ch]
          const payload = s.trackedUrlId
            ? { id: s.trackedUrlId, code: newSlug, long_url: deepLink || '' }
            : this._buildLinkPayload(idx, ch, newSlug, deepLink)
          promises.push(
            api.buildRequest('saveEntryPointLink', 'POST', payload)
              .then(res => {
                const saved = res.data && res.data.payload
                if (saved && saved.id && !s.trackedUrlId) this._patchSu(idx, ch, { trackedUrlId: saved.id })
              })
              .catch(err => console.error('[OutputSidebar] slug update on keyword change failed', idx, ch, err))
          )
        })
      })
      await Promise.all(promises)
    },

    // ── Short URL helpers ──
    getShortUrl (idx, ch) {
      const s = this.shortUrlState[idx] && this.shortUrlState[idx][ch]
      return s ? `${this.urlPrefix}${s.currentSlug}` : ''
    },
    _patchSu (idx, ch, patch) {
      if (!this.shortUrlState[idx]) this.$set(this.shortUrlState, idx, {})
      const current = this.shortUrlState[idx][ch] || {}
      this.$set(this.shortUrlState[idx], ch, { ...current, ...patch })
    },
    toggleCustomUrl (idx, ch) {
      const s = this.shortUrlState[idx] && this.shortUrlState[idx][ch]
      if (!s) return
      const next = !s.useCustom
      this._patchSu(idx, ch, { useCustom: next, customSlug: s.currentSlug, showWarning: false, duplicateError: null })
    },
    onSlugInput (idx, ch, val) {
      this._patchSu(idx, ch, { customSlug: val, showWarning: false, duplicateError: null })
    },
    applyShortUrl (idx, ch) {
      const s = this.shortUrlState[idx][ch]
      if (s.customSlug === s.currentSlug) {
        this.confirmShortUrl(idx, ch)
        return
      }
      this._patchSu(idx, ch, { showWarning: true })
      this.$emit('short_url_change_warned', { entry_point_id: this.kw && this.kw.id, phrase_index: idx, channel: ch })
    },
    async confirmShortUrl (idx, ch) {
      const s = this.shortUrlState[idx][ch]
      const newSlug = s.customSlug
      this._patchSu(idx, ch, { showWarning: false, duplicateError: null })
      const deepLink = this.deepLinks[idx] && this.deepLinks[idx][ch]
      const payload = s.trackedUrlId
        ? { id: s.trackedUrlId, code: newSlug, long_url: deepLink || '' }
        : this._buildLinkPayload(idx, ch, newSlug, deepLink)
      try {
        const res = await api.buildRequest('saveEntryPointLink', 'POST', payload)
        const body = res.data
        if (!body || body.status === false) {
          const message = (body && body.payload && body.payload.message) || 'This short URL is already in use. Please choose a different one.'
          this._patchSu(idx, ch, { duplicateError: message })
          return
        }
        const saved = body.payload
        this._patchSu(idx, ch, { currentSlug: newSlug })
        if (saved && saved.id && !s.trackedUrlId) this._patchSu(idx, ch, { trackedUrlId: saved.id })
        this.$emit('short_url_change_confirmed', { entry_point_id: this.kw && this.kw.id, phrase_index: idx, channel: ch, new_slug: newSlug })
        // Regenerate short URL QR with new slug
        this.$nextTick(() => this.regenerateQRForPhrase(idx))
        this._patchSu(idx, ch, { justSaved: true })
        clearTimeout(this._slugSavedTimers && this._slugSavedTimers[`${idx}_${ch}`])
        if (!this._slugSavedTimers) this._slugSavedTimers = {}
        this._slugSavedTimers[`${idx}_${ch}`] = setTimeout(() => this._patchSu(idx, ch, { justSaved: false }), 3000)
      } catch (err) {
        console.error('[OutputSidebar] slug update failed', err)
        this._patchSu(idx, ch, { duplicateError: 'Failed to update short URL. Please try again.' })
      }
    },
    cancelShortUrl (idx, ch) {
      const s = this.shortUrlState[idx][ch]
      this._patchSu(idx, ch, { customSlug: s.currentSlug, showWarning: false, duplicateError: null })
      this.$emit('short_url_change_cancelled', { entry_point_id: this.kw && this.kw.id, phrase_index: idx, channel: ch })
    },

    // ── QR logo toggle ──
    async toggleNiviLogo (phraseIdx, ch) {
      const current = (this.niviLogoState[phraseIdx] && this.niviLogoState[phraseIdx][ch]) || false
      if (!this.niviLogoState[phraseIdx]) this.$set(this.niviLogoState, phraseIdx, {})
      this.$set(this.niviLogoState[phraseIdx], ch, !current)
      await this.regenerateQRForPhrase(phraseIdx)
    },

    async compositeLogoOnQr (qrDataUrl, width) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = width
        const ctx = canvas.getContext('2d')
        const qrImg = new Image()
        qrImg.onload = () => {
          ctx.drawImage(qrImg, 0, 0, width, width)
          const logo = new Image()
          logo.onload = () => {
            const aspectRatio = logo.naturalWidth / logo.naturalHeight
            // Cap logo height to 18% of QR width; derive width from aspect ratio
            const logoH = Math.round(width * 0.13)
            const logoW = Math.round(logoH * aspectRatio)
            const pad = Math.round(width * 0.025)
            const bgW = logoW + pad * 2
            const bgH = logoH + pad * 2
            const bgX = Math.round((width - bgW) / 2)
            const bgY = Math.round((width - bgH) / 2)
            const radius = Math.round(bgH * 0.2)
            ctx.fillStyle = '#ffffff'
            ctx.beginPath()
            ctx.roundRect(bgX, bgY, bgW, bgH, radius)
            ctx.fill()
            ctx.drawImage(logo, bgX + pad, bgY + pad, logoW, logoH)
            resolve(canvas.toDataURL('image/png'))
          }
          logo.onerror = () => resolve(qrDataUrl)
          logo.src = '/static/img/asknivi-logo-light.png'
        }
        qrImg.onerror = () => resolve(qrDataUrl)
        qrImg.src = qrDataUrl
      })
    },

    // ── Download QR ──
    downloadQr (phraseIdx, ch) {
      const qr = this.qrCodes[phraseIdx] && this.qrCodes[phraseIdx][ch]
      if (!qr || !qr.download) return
      const link = document.createElement('a')
      const phrase = this.drafts.phrases[phraseIdx] || 'phrase'
      link.download = `qr-${ch}-${phrase}.png`
      link.href = qr.download
      link.click()
      this.$emit('output_downloaded', {
        entry_point_id: this.kw && this.kw.id,
        channel: ch,
        phrase_index: phraseIdx,
        logo_included: (this.niviLogoState[phraseIdx] && this.niviLogoState[phraseIdx][ch]) || false,
        timestamp: Date.now()
      })
    },

    downloadShortQr (phraseIdx, ch) {
      const qr = this.shortQrCodes[phraseIdx] && this.shortQrCodes[phraseIdx][ch]
      if (!qr || !qr.download) return
      const link = document.createElement('a')
      const phrase = this.drafts.phrases[phraseIdx] || 'phrase'
      link.download = `qr-short-${ch}-${phrase}.png`
      link.href = qr.download
      link.click()
      this.$emit('output_downloaded', {
        entry_point_id: this.kw && this.kw.id,
        channel: ch,
        phrase_index: phraseIdx,
        qr_type: 'short_url',
        logo_included: (this.niviLogoState[phraseIdx] && this.niviLogoState[phraseIdx][ch]) || false,
        timestamp: Date.now()
      })
    },

    // ── Metadata ──
    async fetchMetadataOptions () {
      try {
        const [useRes, topicRes, campaignRes] = await Promise.all([
          api.buildRequest('/getKeywordUsageTypes', 'POST'),
          api.buildRequest('/getTopics', 'POST'),
          api.buildRequest('/getCampaignData', 'POST')
        ])
        if (useRes.data && useRes.data.payload) this.keywordUseList = useRes.data.payload
        if (topicRes.data && topicRes.data.payload) this.topicList = topicRes.data.payload
        if (campaignRes.data && campaignRes.data.payload) {
          const raw = campaignRes.data.payload
          this.campaignList = Array.isArray(raw)
            ? raw.map(item => (typeof item === 'string' ? { name: item } : item))
            : []
        }
      } catch (err) {
        console.error('[OutputSidebar] fetchMetadataOptions failed', err)
      }
    },

    generateMetaCampaignCode () {
      const chars = '123456789abcdefghijklmnopqrstuvwxyz'
      let code = ''
      for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length))
      const prefix = this.kw && this.kw.client_id ? String(this.kw.client_id).substring(0, 3) : ''
      this.metaDrafts.campaignCode = prefix ? `${prefix}-${code}` : code
      this.metaDrafts.utm_campaign = code
    },

    async saveMetadata () {
      if (!this.kw) return
      this.metaSaving = true
      try {
        const overrides = {
          keyword_usage: this.metaDrafts.keyword_usage,
          topic: this.metaDrafts.topic,
          campaign: this.metaDrafts.campaign_name,
          campaignCode: this.metaDrafts.campaignCode,
          utm_source: this.metaDrafts.utm_source,
          utm_medium: this.metaDrafts.utm_medium,
          utm_campaign: this.metaDrafts.utm_campaign
        }
        const payload = this.buildKeywordPayload({})
        payload.updatedRow.data = { ...payload.updatedRow.data, ...overrides }
        await api.buildRequest('/updateKeyword', 'POST', payload)
        this.stampUpdatedBy()
        clearTimeout(this.metaSavedTimer)
        this.metaSaved = true
        this.metaSavedTimer = setTimeout(() => { this.metaSaved = false }, 3000)
      } catch (err) {
        console.error('[OutputSidebar] saveMetadata failed', err)
      } finally {
        this.metaSaving = false
      }
    },

    // ── Copy ──
    copy (value, outputType, channel, key) {
      if (!value) return
      navigator.clipboard && navigator.clipboard.writeText(value)
      if (key) {
        this.copiedKey = key
        clearTimeout(this.copiedTimer)
        this.copiedTimer = setTimeout(() => { this.copiedKey = null }, 2000)
      }
      this.$emit('output_copied', {
        entry_point_id: this.kw && this.kw.id,
        output_type: outputType,
        channel: channel || null,
        timestamp: Date.now()
      })
    }

  },

  mounted () {
    this.fetchMetadataOptions()
  }
}
</script>
<style scoped src="./OutputSidebar.css"></style>



// WEBPACK FOOTER //
// OutputSidebar.vue?0161c45d